import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from '../routing';
import { analysisApi, formatCurrency, formatCurrencyWhole } from '../utils/api';
// One track colour behind every bar on /analysis — see 07-conventions. This
// card's own #f4f6f8 was the second one, and it sat directly above two cards
// using this token, so "the same bar" came in two greys down one page.
import { TRACK_BG } from '../utils/targetBar';
import { useAuth } from '../context/AuthContext';
import { useIsMobile } from '../utils/useIsMobile';
import { periodLabel, PRESETS_ANALYTIC, activePresetKey, withPeriodParams } from '../utils/period';
import { buildClassificationSlices, pctLabel } from '../utils/classificationSlices';
import { UNTAGGED_COLOR } from '../utils/classificationColors';
import Icon from './Icon';
import InlineLoadError from './InlineLoadError';
import ClassificationSpendEmptyState from './ClassificationSpendEmptyState';
import ChartTooltip, { useHoverTip } from './ChartTooltip';

// By-classification procurement analytics — a SUMMARY card, matching the by-supplier
// card beside it. The full table and the per-supplier drill live on
// /analysis/reports/item-classifications; this card answers "where is the money going"
// at a glance and hands off depth.
//
// Two things it does that its predecessor did not, both load-bearing:
//
// 1. "לא מסווג" IS NOT A BAR. It used to be the largest wedge of a pie —
//    a chart whose headline answer was "unknown", diluting every real
//    classification's share along the way. It now closes the list as a plain
//    summary line, in shekels and as a share of the PERIOD total (which it
//    says outright, because the bars above are shares of CLASSIFIED spend
//    and sum to 100%). A coverage meter sat at the top of this card for a
//    while and was dropped as redundant: it restated, as a percentage, the
//    same fact this line carries in the units the reader is already in.
//
// 2. THE CTA ACTS ON `untagged`, NOT ON THE WHOLE GREY. The server's לא מסווג
//    row merges spend that can be tagged with spend that cannot (invoice lines
//    matching no catalog row). Sending someone to the tagging screen with the
//    combined figure promises a backlog that can never reach ₪0. The two are
//    now named separately, and only the reachable half carries the button.
//
// There is deliberately no monthly trend here. It was a 12-column table that
// scrolled sideways, and the data does not support the shape: production
// classifications appear in as few as 3 of 7 months with 33–163% month-to-month
// volatility, so its gaps read as spending drops when they are upload gaps.
// The 12 months remain in the Excel export, where a reader can judge for
// themselves.
export default function ClassificationAnalysis({ params, filterActive = false }) {
  const { canEdit, isAdmin } = useAuth();
  const navigate = useNavigate();
  // 640, the content-density breakpoint (tables/stats) — not the 720 one the
  // filter bars use. What changes below the line is how much number each row
  // carries, which is a density decision, not a filter-layout one.
  const isMobile = useIsMobile(640);
  const [data, setData] = useState({ total: 0, classifications: [] });
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [titleHover, setTitleHover] = useState(false);
  // Hover readout over a bar row, desktop only — see the tooltip block below.
  const barsRef = useRef(null);
  const { tip, show: showTip, hide: hideTip } = useHoverTip(barsRef);

  const key = JSON.stringify(params);

  const fetchData = useCallback(() => {
    let alive = true;
    setLoading(true);
    setLoadError(false);
    analysisApi.getClassificationSpend(params)
      .then(s => { if (alive) setData(s.data); })
      // A failed fetch must NEVER fall through to the empty state. "אין נתונים"
      // on a network error tells a user their spend is zero, which is a lie
      // they can act on — this is exactly what InlineLoadError exists for.
      .catch(() => { if (alive) { setLoadError(true); setData({ total: 0, classifications: [] }); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(fetchData, [fetchData]);

  // `total` (classified spend) is intentionally not destructured: it is still
  // the denominator every slice's `pct` is computed against inside the util,
  // but the card no longer prints it as a figure of its own.
  const { slices, negativeCount, hasData } = buildClassificationSlices(data.classifications);

  // The three-way split of the SAME window the bars measure. Absent in the
  // admin cross-tenant view, where the scan is too expensive to run — the card
  // then shows bars without a meter rather than failing.
  const coverage = data.coverage;
  const untagged = coverage ? coverage.untagged : 0;
  const untaggable = coverage ? coverage.untaggable : 0;
  const period = periodLabel(params?.date_from, params?.date_to);

  // A tip must not outlive the row it points at: when the period or the
  // supplier filter moves the list under it, a stale index would otherwise
  // read out the wrong classification's money under the right name.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { hideTip(); }, [data]);
  // Defensive for the render BEFORE that effect runs — `?? null` falls back to
  // no tooltip rather than to `undefined.value`.
  const activeSlice = tip ? (slices[tip.i] ?? null) : null;

  const goToReport = () => {
    // /analysis's presets, carried as `?period=<key>` when one is active. The
    // page keeps its range current (usePresetPeriod), so matching now is sound.
    const key = activePresetKey(params?.date_from, params?.date_to, PRESETS_ANALYTIC);
    const qs = withPeriodParams(new URLSearchParams(), key, params?.date_from, params?.date_to);
    // Carried through so the report shows the SAME money as the card that was
    // just clicked. Without it, a filtered card would open an unfiltered
    // report and the two totals would disagree with nothing explaining why.
    if (params?.supplier_ids) qs.set('supplier_ids', params.supplier_ids);
    const s = qs.toString();
    navigate(`/analysis/reports/item-classifications${s ? `?${s}` : ''}`);
  };

  const header = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 16 }}>
      <div>
        <h3 style={{ margin: 0 }}>
          <button
            type="button"
            className="card-title-link"
            onClick={goToReport}
            onMouseEnter={() => setTitleHover(true)}
            onMouseLeave={() => setTitleHover(false)}
            aria-label="צפייה בדוח המלא לפי סיווג פריטים"
            style={{
              font: 'inherit', fontSize: '1rem', fontWeight: 700, color: 'var(--ink-muted)',
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              textDecoration: titleHover ? 'underline' : 'none',
            }}
          >רכש לפי סיווג פריטים</button>
        </h3>
        {/* The card's VAT basis, stated once. It used to live in the totals
            strip; with that gone this is the only place it appears, and it
            cannot simply be dropped — the budget card immediately above
            reports כולל מע״מ, and an unlabelled figure between labelled ones
            gets read as whichever basis the reader saw last (~17% out). */}
        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 3 }}>ללא מע״מ</div>
      </div>
      <div className="page-menu" style={{ position: 'relative' }}>
        <button
          className="page-menu-trigger"
          aria-label="פעולות נוספות"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(o => !o)}
        >⋮</button>
        {menuOpen && (
          <>
            <div className="menu-backdrop" onClick={() => setMenuOpen(false)} />
            <div className="page-menu-panel" style={{ minWidth: 280 }}>
              <button
                className="page-menu-item"
                onClick={() => { setMenuOpen(false); goToReport(); }}
                title="הטבלה המלאה של הרכש לפי סיווג"
              >
                <Icon name="list" size={15} /> צפייה בדוח המלא
              </button>
              {/* The management screen moved in here. On the card's face it was
                  a second visible button competing with the לצפייה CTA, and it
                  is not what a reader of this card came to do. */}
              {canEdit && (
                <button
                  className="page-menu-item"
                  onClick={() => { setMenuOpen(false); navigate('/item-classifications'); }}
                  title="הגדרת סיווגים ושיוך מוצרים"
                >
                  <Icon name="folder" size={15} /> ניהול סיווג פריטים
                </button>
              )}
              <div style={{ borderTop: '1px solid #eef2f7', margin: '6px 0', padding: '10px 4px 0' }}>
                <div style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>איך מחושב</div>
                <p style={{ margin: 0, fontSize: 12.5, color: '#475569', lineHeight: 1.6 }}>
                  העמודות מציגות את חלוקת הרכש <b>המסווג</b> בין הסיווגים בתקופה
                  שנבחרה, <b>ללא מע״מ</b>. האחוזים מחושבים מסך הרכש המסווג, כך שהם
                  מסתכמים ב-100%. שורת <b>לא מסווג</b> שבתחתית הרשימה מדווחת בנפרד,
                  ובאחוז <b>מסך הרכש</b> — ולכן אינה חלק מאותם 100%. שורות ללא מחיר
                  אינן נכללות בחישוב.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="card">
        {header}
        <div className="loading-container"><div className="spinner" /><span>טוען...</span></div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="card">
        {header}
        <InlineLoadError message="לא ניתן לטעון את נתוני הסיווגים" onRetry={fetchData} />
      </div>
    );
  }

  if (!hasData) {
    return (
      <div className="card">
        {header}
        <ClassificationSpendEmptyState
          period={period}
          filterActive={filterActive}
          total={data.total}
          // `0` from a present coverage block is a FACT (nothing left to tag),
          // not a missing value — so it must not fall back to the total. Only
          // an absent block (cross-tenant view) has nothing better to offer.
          untaggedSpend={coverage ? untagged : data.total}
          hasRows={(data.classifications || []).length > 0}
          canEdit={canEdit}
          isAdmin={isAdmin}
        />
      </div>
    );
  }

  // The whole grey bucket (untagged + untaggable), as the server reports it.
  // Taken from the classification rows rather than from `coverage` so the summary
  // line below still renders in the admin cross-tenant view, where the
  // coverage scan is too expensive to run.
  const untaggedRow = (data.classifications || []).find(c => c.classification_id == null);
  const untaggedTotal = untaggedRow ? untaggedRow.spend : 0;
  const untaggedPctOfTotal = data.total > 0 ? (untaggedTotal / data.total) * 100 : 0;

  return (
    <div className="card">
      {header}

      {/* Ranked bars, not a ring. Length compares; a 6%-vs-8% arc does not —
          and with 14–19 classifications a pie degenerates into unreadable slivers.

          ON DESKTOP each row has a hover readout. The row itself prints a
          rounded whole-shekel figure; the tooltip repeats it unrounded
          (formatCurrency vs. formatCurrencyWhole) for anyone tying the
          number out against another report.

          ON MOBILE the row prints the shekel figure ONLY. The bar's length is
          already the share — drawn against the largest classification — so printing
          "· 24.8%" beside it mostly restates what the reader just saw, and at
          375px that repetition was the difference between a scannable list and
          a wall. Shekels are what survives the cut rather than the percent
          because length can express a proportion and cannot express a
          magnitude: drop the ₪ and the row can no longer answer the question
          this card exists for. The exact percentages are one tap away in the
          full report, which prints both. */}
      <div ref={barsRef} style={{ margin: '4px 0 0', position: 'relative' }}>
        {slices.map((s, i) => (
          <div
            key={s.classification_id ?? 'remainder'}
            onMouseEnter={isMobile ? undefined : (e) => showTip(i, e.currentTarget)}
            onMouseLeave={isMobile ? undefined : hideTip}
            style={{
              display: 'grid', gridTemplateColumns: 'minmax(72px, 108px) 1fr auto',
              gap: 10, alignItems: 'center', padding: '5px 0', fontSize: 12.5,
              opacity: s.isRemainder ? 0.75 : 1,
            }}
          >
            <span
              title={s.name}
              style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}
            >
              <span style={{
                width: 9, height: 9, borderRadius: 2, background: s.color, flex: '0 0 auto',
              }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {s.name}
              </span>
            </span>
            <span className="class-bar-track" style={{ background: TRACK_BG, borderRadius: 4, height: 15, overflow: 'hidden' }}>
              <span style={{
                display: 'block', height: '100%', borderRadius: 4, background: s.color,
                // Share of the LARGEST bar, so the biggest row fills the track
                // and the rest read as a proportion of it. Against the total
                // instead, a flat taxonomy would draw every bar as a stub.
                width: `${slices[0].value ? (s.value / slices[0].value) * 100 : 0}%`,
              }} />
            </span>
            <span style={{
              color: 'var(--ink-muted)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
            }}>
              {formatCurrencyWhole(s.value)}
              {!isMobile && ` · ${pctLabel(s.pct)}`}
            </span>
          </div>
        ))}

        {/* The gap, closing the list — deliberately NOT a bar.
            It is the one line here that is not a classification: giving it a bar
            would put "unknown" back into the ranking, usually at the top,
            and every other bar's share would silently be measured against a
            different denominator than the one it is drawn against.
            Its percentage is of the PERIOD total, not the classified total the
            bars above sum to — a real difference (this row and the bars can
            legitimately disagree on what "the whole" is) — but the row is set
            apart by the divider and the grey rather than stated in words, so
            its own line reads the same "amount · percent" shape as every bar
            above it instead of carrying a suffix none of them do. */}
        {untaggedTotal > 0 && (
          <div style={{
            display: 'grid', gridTemplateColumns: 'minmax(72px, 108px) 1fr auto',
            gap: 10, alignItems: 'center', marginTop: 6, paddingTop: 8,
            borderTop: '1px solid #eef1f5', fontSize: 12.5, color: 'var(--ink-muted)',
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <span style={{
                width: 9, height: 9, borderRadius: 2, background: UNTAGGED_COLOR, flex: '0 0 auto',
              }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                לא מסווג
              </span>
            </span>
            {/* The action rides on the figure it acts on. It used to sit in an
                orange band below, restating this same number — they differ
                only by `untaggable`, which production measures at ~0.5% of
                spend, so the band was a second copy of this line plus a
                button. */}
            <span>
              {canEdit && untagged > 0 && (
                <button
                  type="button"
                  onClick={() => navigate('/item-classifications?untagged=1')}
                  style={{
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    font: 'inherit', fontSize: 12.5, fontWeight: 600, color: '#0f3460',
                    textDecoration: 'underline', textUnderlineOffset: 2,
                  }}
                >שייך פריטים</button>
              )}
            </span>
            <span style={{
              color: 'var(--ink-muted)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
            }}>
              {formatCurrencyWhole(untaggedTotal)}
              {!isMobile && ` · ${pctLabel(untaggedPctOfTotal)}`}
            </span>
          </div>
        )}

        {/* Desktop-only, and deliberately not given a tap equivalent: hover does
            not exist on touch, and a four-line panel is the opposite of what the
            mobile rows above were just thinned out for. Phones reach the same
            figures through the full report.

            Same shape and wording as the by-supplier card's tooltip, down to the
            label column — the two sit on one screen, and a reader who learned
            the readout on one should not have to re-learn it on the other. */}
        {/* "מהרכש המסווג", not the donut's "מהרכש": these bars are shares of
            CLASSIFIED spend and sum to 100%, which is a different whole from
            the one the לא מסווג line below reports against. */}
        {activeSlice && (
          <ChartTooltip
            tip={tip}
            title={activeSlice.name}
            titleNote={activeSlice.isRemainder ? `${activeSlice.count} סיווגים` : null}
            rows={[
              { label: 'ללא מע״מ', value: formatCurrency(activeSlice.value) },
              { label: 'מהרכש המסווג', value: pctLabel(activeSlice.pct) },
            ]}
          />
        )}
      </div>

      {/* Both bands that used to sit here are gone.
          The orange one restated the לא מסווג line's own figure — they differ
          only by `untaggable` (~0.5% of spend in production) — so it was a
          duplicate carrying a button, and the button moved onto the line.
          The sage strip reported "סה״כ רכש מסווג", which is a bookkeeping
          artifact rather than a figure anyone manages against: no one budgets
          the portion of spend they happen to have tagged. Each bar already
          carries its own shekels, and the period total is on the by-supplier
          card. Its one irreplaceable job — naming the VAT basis — moved to
          the header subtitle, where it is stated once. */}
      {negativeCount > 0 && (
        <p style={{ margin: '8px 0 0', fontSize: 11.5, color: '#94a3b8', lineHeight: 1.55 }}>
          {negativeCount === 1 ? 'סיווג אחד בסכום שלילי (זיכויים) מוצגת' : `${negativeCount} סיווגים בסכום שלילי (זיכויים) מוצגות`} בדוח המלא בלבד.
        </p>
      )}

      {/* Shown whenever it exists, unlike the catalog band which saves it for
          the done state. There, it answers "why is this not 100%" at the end;
          here the reader is asking "why is so much unknown" right now. */}
      {untaggable > 0 && (
        <p style={{ margin: '8px 0 0', fontSize: 11.5, color: '#94a3b8', lineHeight: 1.55 }}>
          {formatCurrencyWhole(untaggable)} לא ניתן לשיוך — שורות בחשבונית שלא נמצא
          להן מוצר תואם בקטלוג.
        </p>
      )}

      <div style={{ marginTop: 12, paddingTop: 11, borderTop: '1px solid #eef1f5' }}>
        <button
          type="button"
          onClick={goToReport}
          style={{
            width: '100%', minHeight: 44, display: 'flex', alignItems: 'center',
            justifyContent: 'flex-end', background: 'none', border: 'none',
            padding: 0, cursor: 'pointer', font: 'inherit',
          }}
        >
          <span className="btn btn-sm btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            לצפייה <Icon name="chevron" size={13} style={{ transform: 'rotate(90deg)' }} />
          </span>
        </button>
      </div>
    </div>
  );
}
